import {
    Component, cookies,
    element as $,
    elements as $$,
    KEY_ARROW_DOWN,
    KEY_ARROW_LEFT,
    KEY_ARROW_RIGHT,
    KEY_ARROW_UP,
    KEY_ENTER,
    KEY_ESCAPE,
    KEY_TAB,
} from '@contorion/core';
import { SearchService } from '../services/search.service';
import { SEARCH_COOKIE } from '../constants/search.constants';

const CLASS_ACTIVE: string = 'active';
const CLASS_HIDDEN: string = 'hidden';
const CLASS_FIXED: string = 'fixed';
const SELECTOR_OVERLAY: string = '.js-search-bar-overlay';
const SELECTOR_HEADER: string = '.js-search-bar-header';
const SELECTOR_CLOSE_BUTTON: string = '.js-close-button';
const SELECTOR_REMOVE_SAVED: string = '.js-remove-saved-search';
const SELECTOR_SEARCH_FLYOUT_V2: string = 'js-search-flyout-v2';
const SELECTOR_RESULT_LIST_V2: string = '.js-search-result-list-v2';

export class ResultListComponent extends Component {
    private _column: number;
    private _row: number;

    protected get overlay(): HTMLElement {
        return <HTMLElement> $(SELECTOR_OVERLAY);
    }

    protected get header(): HTMLElement {
        return <HTMLElement> $(SELECTOR_HEADER);
    }

    protected get closeButton(): HTMLElement {
        return <HTMLElement> $(SELECTOR_CLOSE_BUTTON);
    }

    protected get removeSaved(): HTMLElement {
        return <HTMLElement> $(SELECTOR_REMOVE_SAVED, this.element);
    }

    protected get resultListElementV2(): HTMLElement {
        return <HTMLElement> $(SELECTOR_RESULT_LIST_V2);
    }

    get column(): number {
        return this._column === undefined ? 0 : this._column;
    }

    set column(column: number) {
        this._column = column;

        if (this.column + 1 > this.columns.length) {
            this.column = 0;
        } else if (this.column < 0) {
            this.column = Math.max(this.columns.length - 1, 0);
        } else {
            this.row = this.row;
        }
    }

    get row(): number {
        return this._row === undefined ? -1 : this._row;
    }

    set row(row: number) {
        if (!this.rows.length) {
            return;
        }

        this._row = row;

        if (row + 1 > this.rows.length) {
            this._row = 0;
        } else if (row < 0) {
            this._row = this.rows.length - 1;
        }

        this.updateSelection();
    }

    get columns(): Array<Element> {
        return $$('ul', this.element);
    }

    get rows(): Array<Element> {
        const columns = this.columns;

        if (!columns.length || typeof columns[this.column] === 'undefined') {
            return [];
        }

        return $$('li[data-original-value]', columns[this.column]);
    }

    get content() {
        return this.element.innerHTML;
    }

    set content(value) {
        if (this.element === null && this.resultListElementV2 === null) {
            return;
        }

        //TODO Remove/replace after BOSS-20671 the AB test New search flyout for desktop is finished
        if (value.includes(SELECTOR_SEARCH_FLYOUT_V2)) {
            this.resultListElementV2.innerHTML = value;
            if (value.length) {
                this.resultListElementV2.classList.remove(CLASS_HIDDEN);
                this.overlay?.classList.remove(CLASS_HIDDEN);
                this.element.classList.add(CLASS_HIDDEN);
            } else {
                this.resultListElementV2.classList.add(CLASS_HIDDEN);
                this.overlay?.classList.add(CLASS_HIDDEN);
            }
        } else {
            this.element.innerHTML = value;
            if (value.length) {
                this.element.classList.remove(CLASS_HIDDEN);
                this.overlay?.classList.remove(CLASS_HIDDEN);
                this.header?.classList.add(CLASS_ACTIVE);
                this.closeButton.classList.remove(CLASS_HIDDEN);
                document.body.classList.add(CLASS_FIXED);
                this.resultListElementV2?.classList.add(CLASS_HIDDEN);
            } else {
                this.element.classList.add(CLASS_HIDDEN);
                this.overlay?.classList.add(CLASS_HIDDEN);
                document.body.classList.remove(CLASS_FIXED);
                this.header.classList.remove(CLASS_ACTIVE);
            }
        }
    }

    constructor(
        protected element: Element,
        protected service: SearchService,
    ) {
        super(element);

        this.initEvents();
    }

    public reset() {
        this._row = -1;
        this._column = 0;
        this.content = '';
    }

    protected initEvents() {
        this.service.result.subscribe((result: string) => {
            if (result) {
                this.content = result;
                if (this.removeSaved) {
                    this.removeSaved.addEventListener('click', this.onRemoveSaved.bind(this));
                }
            }
        });

        this.service.value.subscribe((value: string) => {
            if (!value.length) {
                this.reset();
            }
        });

        this.service.keyboardEvent.subscribe(this.onKeyboardEvent.bind(this));
    }

    protected onKeyboardEvent(e: KeyboardEvent) {
        if (e === null || !this.content.length) {
            return;
        }

        switch (e.keyCode) {
            case KEY_ARROW_UP:
                this.goRowUp();

                break;
            case KEY_ARROW_DOWN:
                this.goRowDown();

                break;
            case KEY_TAB:
                this.goRowDown();

                break;
            case KEY_ARROW_RIGHT:
                this.goColumnRight();

                break;
            case KEY_ARROW_LEFT:
                this.goColumnLeft();

                break;
            case KEY_ESCAPE:
                this.reset();

                break;
            case KEY_ENTER:
                if (this.row > -1) {
                    this.goToSelection();
                }

                break;
            default:

                break;
        }
    }

    protected goRowUp() {
        if (this.row > -1) {
            this.row--;
        }
    }

    protected goRowDown() {
        this.row++;
    }

    protected goColumnRight() {
        if (this.row > -1) {
            this.column++;
        }
    }

    protected goToSelection() {
        window.location.href = $('a', this.rows[this.row]).getAttribute('href');
    }

    protected goColumnLeft() {
        if (this.row > -1) {
            this.column--;
        }
    }

    protected updateSelection() {
        const { row, rows } = this;
        const activeElement = $('li.active', this.element);

        if (activeElement) {
            activeElement.classList.remove(CLASS_ACTIVE);
        }

        rows[row].classList.add(CLASS_ACTIVE);

        this.service.value.next(rows[row].getAttribute('data-original-value'));
    }

    protected onRemoveSaved(): void {
        cookies.remove(SEARCH_COOKIE.searchHistory);
        this.service.search();
    }
}
